import React from "react";
import { useRecommendationStore } from "../../stores/recommendationStore";
import { useDocumentGeneratorStore } from "../../stores/documentGeneratorStore";
import {
  Icon,
  InfoModal,
  PermissionWrapper,
  RowActionsMenu,
  useConfirmationStore,
} from "components";
import { useAuth } from "hooks/useAuth";
import { noImage, cashIcon, debitIcon, plusCircleIcon } from "assets";
import RecommendationTablePriceCell from "./RecommendationTablePriceCell";
import RecomendationEditableInfoCell from "./RecomendationEditableInfoCell";
import RecommendationIncomingBadge from "./RecommendationIncomingBadge";
import { ROZETKA_WAREHOUSE_ID } from "../../utils/warehouses";
import { formatUah, formatProcent } from "utils/format";

// Компонент для відображення рядка таблиці
const RecommendationTableRow = ({
  item,
  editing,
  canEditPrices,
  canEditComments,
  onImageClick,
  onStartEditing,
  onUpdateValue,
  onEditChange,
  onCancelEditing,
  onDelete,
  onEditWholesaleMarkups,
}) => {
  // Отримуємо дані та дії зі стору рекомендацій
  // Нульові залишки показуються з фільтром «Нульові залишки» або «Очікуваний
  // прихід»: другий теж віддає товари з нулем, які їдуть на склад
  const is_full = useRecommendationStore(
    (state) => state.filters.is_full || state.filters.in_transit,
  );
  const warehouse_id = useRecommendationStore((state) => state.filters.warehouse_id);
  const isRozetka = warehouse_id === ROZETKA_WAREHOUSE_ID;
  const priceViewMode = useRecommendationStore(
    (state) => state.viewSettings.priceViewMode,
  );
  const customTags = useRecommendationStore((state) => state.customTags);
  const assignCustomTag = useRecommendationStore((state) => state.assignCustomTag);
  const removeCustomTag = useRecommendationStore((state) => state.removeCustomTag);

  // Отримуємо дані та дії зі стору призначення цін
  const isSelectionModeActive = useDocumentGeneratorStore(
    (state) => state.isSelectionModeActive,
  );
  const selectedSkus = useDocumentGeneratorStore((state) => state.selectedSkus);
  const activeDocumentType = useDocumentGeneratorStore(
    (state) => state.activeDocumentType,
  );
  const addSku = useDocumentGeneratorStore((state) => state.addSku);
  const removeSku = useDocumentGeneratorStore((state) => state.removeSku);

  // Достаємо методи зі стору підтвердження
  const { showConfirmation } = useConfirmationStore.getState();

  // Отримуємо права поточного користувача
  const { isAllowed } = useAuth();
  const canDeletePrices = isAllowed("recommendations:delete-prices");
  const canEditTags = isAllowed("recommendations:edit-custom-tags");

  // Обробник видалення рекомендованої ціни з підтвердженням
  const handleDelete = () => {
    showConfirmation({
      title: "Підтвердити видалення",
      message: `Ви впевнені, що хочете видалити рекомендовану ціну для товару "${item.name || item.article}"? Цю дію не можна скасувати.`,
      onConfirm: onDelete,
    });
  };

  // Мітки товару
  const tags = item.tags || [];

  // Обробник видалення мітки з підтвердженням
  const handleRemoveTag = (tag) => {
    showConfirmation({
      title: "Видалення мітки",
      message: `Видалити мітку "${tag.name}" з товару "${item.name || item.article}"?`,
      onConfirm: () => removeCustomTag(item.article, tag.id),
    });
  };

  // Мітки, які ще можна додати товару
  const availableTags = customTags.filter(
    (tag) => !tags.some((itemTag) => itemTag.id === tag.id),
  );

  // Формуємо список дій рядка відповідно до прав користувача
  const rowActions = [];
  if (canEditTags) {
    availableTags.forEach((tag) => {
      rowActions.push({
        label: `Додати мітку «${tag.name}»`,
        onClick: () => assignCustomTag(item.article, tag.id),
      });
    });
  }
  // Оптові націнки спільні для всіх розділів, але Rozetka оптом не торгує
  if (canEditPrices && !isRozetka) {
    rowActions.push({
      label: item.wholesale_markups
        ? "Змінити націнку оптових (індивідуальна)"
        : "Змінити націнку оптових",
      onClick: () => onEditWholesaleMarkups(item),
    });
  }
  if (canDeletePrices) {
    rowActions.push({
      label: "Видалити рекомендовану ціну",
      onClick: handleDelete,
      disabled: !item.is_reco,
      danger: true,
    });
  }

  // Визначаємо динамічний ліміт і назву документа для підказок
  const isCatalog = activeDocumentType === "catalog";
  const currentLimit = isCatalog ? 255 : 105;
  const docLabel = isCatalog ? "каталогу" : "цінників";

  // Логіка вибору sku для формування документів
  const isChecked = selectedSkus.includes(item.article);
  const isSelectionDisabled = selectedSkus.length >= currentLimit && !isChecked;

  const handleCheckboxChange = () => {
    if (isChecked) {
      removeSku(item.article);
    } else if (!isSelectionDisabled) {
      addSku(item.article);
    }
  };

  // Залишки продаж: у розділі Rozetka це склад Rozetka разом зі складами онлайн магазину
  const saleQuantity = {
    cash: (isRozetka ? item.cash_quantity_sale : item.cash_quantity) || 0,
    debit: (isRozetka ? item.debit_quantity_sale : item.debit_quantity) || 0,
  };

  // Логіка для визначення наявності готівкових/безготівкових даних
  const hasCashDisplay =
    saleQuantity.cash > 0 ||
    (is_full &&
      (item.cash_quantity_total > 0 ||
        item.cash_price > 0 ||
        item.cash_price > item.debit_price));
  const hasDebitDisplay =
    saleQuantity.debit > 0 ||
    (is_full &&
      (item.debit_quantity_total > 0 ||
        item.debit_price > 0 ||
        item.cash_price <= item.debit_price)) ||
    // Товар Rozetka лише з кастомним залишком показуємо безготівковим рядком
    (isRozetka && saleQuantity.cash <= 0);

  // Стан редагування кастомного залишку Rozetka
  const isEditingCustomQuantity =
    editing.article === item.article && editing.field === "custom_quantity";
  const hasCustomQuantity =
    item.custom_quantity !== null && item.custom_quantity !== undefined;

  // Комірка розділу Rozetka з довідковим значенням для готівки та безготівки
  const renderRozetkaInfoCell = (fieldBase, format, className) => (
    <td className={`price split-cell ${className}`}>
      <div className="split-cell-content">
        {hasCashDisplay && (
          <div>
            <span>{format(item[`cash_${fieldBase}`])}</span>
          </div>
        )}
        {hasDebitDisplay && (
          <div>
            <span>{format(item[`debit_${fieldBase}`])}</span>
          </div>
        )}
      </div>
    </td>
  );

  return (
    <tr key={item.article}>
      {isSelectionModeActive && (
        <td className="selection">
          <input
            className="focus-checkbox"
            type="checkbox"
            checked={isChecked}
            disabled={isSelectionDisabled}
            onChange={handleCheckboxChange}
            title={
              isSelectionDisabled ? `Ліміт ${currentLimit} для ${docLabel}` : "Обрати"
            }
          />
        </td>
      )}
      <td className="article">{item.article}</td>

      <td className="photo">
        <img
          src={item.img_url || noImage}
          alt={item.name}
          className={`${item.img_url ? "clickable" : ""}`}
          onClick={() => onImageClick(item.img_url)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
      </td>

      <td className="name">
        <span>{item.name || "---"}</span>
        {item.category && <span className="badge">{item.category}</span>}
        {/* Лід Магніт показуємо лише для товарів на продаж (склад 0) */}
        {warehouse_id === 0 &&
          (Number(item.cash_price_custom) > 0 || Number(item.debit_price_custom) > 0) && (
            <span className="custom-price-tag" title="Кастомна ціна">Лід Магніт</span>
          )}
        {tags.map((tag) =>
          canEditTags ? (
            <button
              key={tag.id}
              type="button"
              className="custom-price-tag removable"
              style={tag.color ? { backgroundColor: tag.color } : undefined}
              title={`${tag.description || tag.name} — натисніть, щоб видалити`}
              onClick={() => handleRemoveTag(tag)}
            >
              {tag.name}
            </button>
          ) : (
            <span
              key={tag.id}
              className="custom-price-tag"
              style={tag.color ? { backgroundColor: tag.color } : undefined}
              title={tag.description || tag.name}
            >
              {tag.name}
            </span>
          ),
        )}
        {/* Товар у дорозі: позначка з підказкою про очікувані приходи */}
        <RecommendationIncomingBadge incoming={item.incoming} />
        <RecomendationEditableInfoCell
          item={item}
          editing={editing}
          isEditable={canEditComments}
          onStartEditing={onStartEditing}
          onEditChange={onEditChange}
          onSave={onUpdateValue}
          onCancel={onCancelEditing}
        />
        {(item.cash_quantity_link > 0 || item.debit_quantity_link > 0) && (
          <a
            href={`/recommendation?warehouse_id=${item.link_warehouse}&search=${item.article}`}
            className="link_to_warehouse"
          >
            %
          </a>
        )}
      </td>

      {/* Залишки буферного складу Rozetka */}
      {isRozetka && (
        <td className="quantity split-cell">
          <div className="split-cell-content">
            {hasCashDisplay && (
              <div>
                <span>{item.cash_quantity || 0}</span>
              </div>
            )}
            {hasDebitDisplay && (
              <div>
                <span>{item.debit_quantity || 0}</span>
              </div>
            )}
          </div>
        </td>
      )}

      {/* Залишки продаж. У розділі Rozetka це склад Rozetka + онлайн магазин,
          а подвійний клік задає кастомний залишок замість фактичного */}
      <td
        className="quantity split-cell"
        onDoubleClick={
          isRozetka && canEditPrices
            ? () => onStartEditing(item, "custom_quantity")
            : undefined
        }
      >
        {isEditingCustomQuantity ? (
          <input
            type="number"
            min="0"
            step="1"
            value={editing.value}
            onChange={onEditChange}
            onBlur={onUpdateValue}
            onKeyDown={(e) => {
              if (e.key === "Enter") onUpdateValue();
              else if (e.key === "Escape") onCancelEditing();
            }}
            autoFocus
          />
        ) : isRozetka && hasCustomQuantity ? (
          <InfoModal
            info={[
              { label: "Фактичний залишок (готівка)", value: saleQuantity.cash },
              { label: "Фактичний залишок (безготівка)", value: saleQuantity.debit },
            ]}
          >
            <span className="custom-quantity">{item.custom_quantity}</span>
          </InfoModal>
        ) : (
          <div className="split-cell-content">
            {hasCashDisplay && (
              <div>
                <span>{saleQuantity.cash}</span>
              </div>
            )}
            {hasDebitDisplay && (
              <div>
                <span>{saleQuantity.debit}</span>
              </div>
            )}
          </div>
        )}
      </td>

      <PermissionWrapper requiredPermission="recommendations:view-stocks">
        <td className="quantity_total split-cell">
          <div className="split-cell-content">
            {hasCashDisplay && (
              <div>
                <span>{item.cash_quantity_total || 0}</span>
              </div>
            )}
            {hasDebitDisplay && (
              <div>
                <span>{item.debit_quantity_total || 0}</span>
              </div>
            )}
          </div>
        </td>
      </PermissionWrapper>

      {/* Довідкові колонки розділу Rozetka: ціна складу 0 та комісія категорії */}
      {isRozetka &&
        renderRozetkaInfoCell("base_price", (value) => formatUah(value) || "---", "price_base")}
      {isRozetka &&
        renderRozetkaInfoCell(
          "commission",
          // Комісія не застосовується, якщо ціна задана вручну
          (value) => (value === null || value === undefined ? "---" : formatProcent(value)),
          "price_commission",
        )}

      {/* Передача даних до PriceCell для статичних колонок */}
      {(isRozetka
        ? [{ fieldBase: "price", managerPercent: 0 }]
        : [
            { fieldBase: "price", managerPercent: 0.01 },
            { fieldBase: "price_11", managerPercent: 0.0075 },
            { fieldBase: "price_custom", managerPercent: 0.005 },
          ]
      ).map(({ fieldBase, managerPercent }) => (
        <RecommendationTablePriceCell
          key={fieldBase}
          item={item}
          fieldBase={fieldBase}
          managerPercent={managerPercent}
          editing={editing}
          onStartEditing={onStartEditing}
          onUpdateValue={onUpdateValue}
          onEditChange={onEditChange}
          onCancelEditing={onCancelEditing}
          isEditable={canEditPrices}
          hasCashData={hasCashDisplay}
          hasDebitData={hasDebitDisplay}
          customStyle={fieldBase}
        />
      ))}

      {/* Динамічна колонка (0.5, День Народження, Опт), у розділі Rozetka відсутня */}
      {isRozetka ? null : priceViewMode === "price_wholesale" ? (
        <PermissionWrapper
          requiredPermission="recommendations:view-wholesale"
          fallback={<td className="price split-cell price_wholesale">---</td>}
        >
          <RecommendationTablePriceCell
            item={item}
            fieldBase="price_wholesale"
            managerPercent={0.025}
            editing={editing}
            onStartEditing={onStartEditing}
            onUpdateValue={onUpdateValue}
            onEditChange={onEditChange}
            onCancelEditing={onCancelEditing}
            isEditable={canEditPrices}
            hasCashData={hasCashDisplay}
            hasDebitData={hasDebitDisplay}
            customStyle="price_wholesale"
          />
        </PermissionWrapper>
      ) : (
        <RecommendationTablePriceCell
          item={item}
          fieldBase={priceViewMode}
          managerPercent={0.01}
          editing={editing}
          onStartEditing={onStartEditing}
          onUpdateValue={onUpdateValue}
          onEditChange={onEditChange}
          onCancelEditing={onCancelEditing}
          isEditable={canEditPrices}
          hasCashData={hasCashDisplay}
          hasDebitData={hasDebitDisplay}
          customStyle={priceViewMode}
        />
      )}

      <td className="price_type split-cell">
        <div className="split-cell-content">
          {hasCashDisplay ? (
            <div>
              <img className="icon" src={cashIcon} alt="cash" title="Готівка" />
            </div>
          ) : (
            canEditPrices && (
              <button onClick={() => onStartEditing(item, "cash_price")}>
                <Icon src={plusCircleIcon} />
              </button>
            )
          )}
          {hasDebitDisplay ? (
            <div>
              <img className="icon" src={debitIcon} alt="debit" title="Безготівка" />
            </div>
          ) : (
            canEditPrices && (
              <button onClick={() => onStartEditing(item, "debit_price")}>
                <Icon src={plusCircleIcon} />
              </button>
            )
          )}
        </div>
      </td>

      <td className="weight">
        {item.actual_weight !== null ? item.actual_weight.toFixed(2) : "---"}
      </td>
      <td className="volumetric_weight">
        {item.volumetric_weight !== null ? item.volumetric_weight.toFixed(2) : "---"}
      </td>

      <PermissionWrapper
        requiredPermission={[
          "recommendations:edit-prices",
          "recommendations:delete-prices",
          "recommendations:edit-custom-tags",
        ]}
      >
        <td className="action-cell">
          <RowActionsMenu actions={rowActions} />
        </td>
      </PermissionWrapper>
    </tr>
  );
};

export default RecommendationTableRow;
